import { useCallback, useContext, useMemo, useState } from 'react';
import UsersContext from '../../../../contexts/UsersContext';
import userTypeCheckers from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';
import STORE from '../../../../store';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import useGrantRevokeRole from '../../../../hooks/useGrantRevokeRole';
import Roles from '../../../../roles';
import appNavItemTypeCheckers from '../../../../types/appNavItemTypeCheckers';

const useBoardOwners = ({
  app,
  signOffBoardNavItem,
}: {
  app: V3.BonusApp;
  signOffBoardNavItem:
    | V3.Navigation.SignOffBoard
    | V3.Navigation.ApprovalsSignOffBoardTab;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { allUsers } = useContext(UsersContext);
  const { grantRole, revokeRole } = useGrantRevokeRole();
  const [isLoading, setIsLoading] = useState(false);

  const owners = useMemo(() => {
    return allUsers
      .filter((u) => signOffBoardNavItem.signOff.ownerIds.includes(u.id))
      .filter(userTypeCheckers.isSignedUpOrPendingUser)
      .filter((u) => !u.isDeleted);
  }, [allUsers, signOffBoardNavItem.signOff.ownerIds]);

  const updateOwners = useCallback(
    async (newOwnerIds: string[]) => {
      let newApp: V3.BonusApp;

      if (signOffBoardNavItem.type === 'Sign Off Board') {
        const newNavItem: V3.Navigation.SignOffBoard = {
          ...signOffBoardNavItem,
          signOff: {
            ...signOffBoardNavItem.signOff,
            ownerIds: newOwnerIds,
          },
        };

        newApp = {
          ...app,
          navigationSettings: {
            ...app.navigationSettings,
            sections: app.navigationSettings.sections.map((s) => {
              if (s.items.some((i) => i.key === signOffBoardNavItem.key)) {
                return {
                  ...s,
                  items: s.items.map((i) => {
                    if (i.key === signOffBoardNavItem.key) {
                      return newNavItem;
                    }
                    return i;
                  }),
                };
              }
              return s;
            }),
          },
        };
      } else {
        newApp = {
          ...app,
          navigationSettings: {
            ...app.navigationSettings,
            sections: app.navigationSettings.sections.map((s) => ({
              ...s,
              items: s.items.map((i) => {
                if (appNavItemTypeCheckers.isApprovals(i)) {
                  return {
                    ...i,
                    tabs: i.tabs.map((tab) => {
                      if (
                        tab.type === 'sign-off-board' &&
                        tab.boardId === signOffBoardNavItem.boardId
                      ) {
                        return {
                          ...tab,
                          signOff: {
                            ...tab.signOff,
                            ownerIds: newOwnerIds,
                          },
                        };
                      }
                      return tab;
                    }),
                  };
                }
                return i;
              }),
            })),
          },
        };
      }

      await STORE.contentDistributions
        .getBonusAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp);
    },
    [app, selectedAccountId, signOffBoardNavItem],
  );

  const onOwnerAdded = useCallback(
    async (user: UserManagement.PendingUser | UserManagement.SignedUpUser) => {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      await grantRole(user.id, Roles.BOARD_OWNER);

      const newOwnerIds = [...owners.map((u) => u.id), user.id];
      await updateOwners(newOwnerIds);
      setIsLoading(false);
    },
    [grantRole, isLoading, owners, updateOwners],
  );

  const onOwnerRemoved = useCallback(
    async (user: UserManagement.PendingUser | UserManagement.SignedUpUser) => {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      await revokeRole(user.id, Roles.BOARD_OWNER);

      const newOwnerIds = owners
        .map((u) => u.id)
        .filter((uid) => uid !== user.id);
      await updateOwners(newOwnerIds);
      setIsLoading(false);
    },
    [isLoading, owners, revokeRole, updateOwners],
  );

  return {
    owners,
    onOwnerAdded,
    onOwnerRemoved,
    isLoading,
  };
};

export default useBoardOwners;
